import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import 'react-loader-spinner/dist/loader/css/react-spinner-loader.css';
import { TailSpin } from 'react-loader-spinner';

import { pollServer } from '../../../api/server';
import { COOKIE } from '../../../constants';
import { TidalButton } from '../../common/button';
import CopyTick from '../../svgs/CopyTick';
import Eye from '../../svgs/Eye';
import { toast } from 'react-toastify';

const TidalDashboardServerCreatedModalWrapper = styled.div`
  border-radius: 14px;
  padding: 24px 28px;
  padding-bottom: 0;
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  box-shadow: 0px 26px 87px rgba(27, 31, 38, 0.25);
  width: 518px;
  max-width: 100%;
`;

const TidalDashboardServerCreatedModalInfoRow = styled.div`
  display: flex;
  grid-gap: 0 12px;
  justify-items: center;
  align-items: center;
  margin: 0 auto;
  margin-top: 32px;
  width: fit-content;
  & > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    & > .text {
      font-weight: 700;
      font-size: 24px;
      line-height: 29px;
      color: ${(props) => props.theme.colors.text[4]};
    }
    & > div {
      justify-content: center;
    }
  }
  & > .icon-wrapper {
    height: 40px;
    width: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => `${props.theme.colors.tags[0]}3d`};
  }
  & > .icon-wrapper-error {
    height: 40px;
    width: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => `${props.theme.colors.tags[4]}3d`};
  }
  & > .text {
    font-weight: 700;
    font-size: 24px;
    line-height: 29px;
    color: ${(props) => props.theme.colors.text[4]};
  }
`;

const TidalDashboardServerCreatedModalTitle = styled.div`
  max-width: 446px;
  margin: 0 auto;
  margin-top: 32px;
  & > .title {
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    text-align: center;
    color: ${(props) => props.theme.colors.text[0]};
    margin-top: 24px;
    margin-bottom: 32px;
    span {
      color: ${(props) => props.theme.colors.text[1]};
    }
  }
`;

const TidalDashboardServerCreatedModalBody = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  padding: 18px 0;
  padding-bottom: 6px;
`;

const TidalDashboardServerCreatedModalBodyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  .left {
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    color: ${(props) => props.theme.colors.text[0]};
  }
  .right {
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    color: ${(props) => props.theme.colors.text[1]};
    display: flex;
    align-items: center;
    & > .key-wrapper {
      margin-right: 4px;
      & > .key-row {
        display: flex;
        align-items: center;
        grid-gap: 4px;

        .obstructed-pattern {
          display: flex;
          grid-gap: 3px;
          wrap: nowrap;
          .cirle {
            height: 5px;
            width: 5px;
            border-radius: 50%;
            background-color: ${(props) => props.theme.colors.text[0]};
          }
        }
      }
    }
  }
`;

const TidalDashboardServerCreatedModalFooter = styled.div`
  padding-top: 16px;
  padding-bottom: 20px;
  border-top: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  display: flex;
  justify-content: flex-end;
`;

const TidalDashboardBotVaultCardObfuscateButton = styled.button``;

interface ITidalDashboardDeleteKeyModal {
  onClose: () => void;
  onServersReady: () => void;
  response: any;
}

const TidalDashboardServerCreatedModal = ({
  onClose,
  onServersReady,
  response,
}: ITidalDashboardDeleteKeyModal): JSX.Element => {
  const [isObfuscated, setIsObfuscated] = React.useState(true);
  const [cookies, _, removeCookie] = useCookies([COOKIE.ACCESS_TOKEN]);
  const [serverReady, setServerReady] = useState(false);
  const [ipAddress, setIpAddress] = useState('');
  const [servers, setServers] = useState<any>(null);
  const [readyServers, setReadyServers] = useState([]);

  const rdpFile = (user, pass, ip): string => {
    let fileData = '';
    fileData += 'full address:s:' + ip + '\n';
    // fdata += "domain:s:"+ domain +"\n";
    fileData += 'username:s:' + user;

    const uri = 'data:text/plain;charset=utf-8,' + encodeURIComponent(fileData);
    // window.open(uri, "RDP FILE");
    return uri;
  };

  const handlePolling = async (data) => {
    console.log('polling', data);
    try {
      const res = await pollServer({
        data,
        provider: servers.provider,
      });
      return res;
    } catch (e) {
      throw e;
    }
  };

  const needsToPoll = (data) => {
    return !data.success;
  };

  const poll = async () => {
    let pollingData = servers;
    if (!pollingData || serverReady) return;
    pollingData = pollingData.map((data) => ({
      ...data,
      auth: {
        user: servers.user,
        pass: servers.pass,
      },
    }));
    try {
      let res = await handlePolling(pollingData);
      if (!res.success) {
        while (true) {
          res = await handlePolling(res.pollingData);
          if (!needsToPoll(res)) {
            setServerReady(true);
            setReadyServers(res.pollingData);
            onServersReady();
            break;
          }
        }
      } else {
        setServerReady(true);
        setReadyServers(res.pollingData);
        onServersReady();
      }
    } catch (e) {
      toast(e.message, {
        type: 'error',
      });
    }
  };

  useEffect(() => {
    let tempResponse = response;
    let mappedResponse = tempResponse.response.map((r) => {
      return {
        ...r,
        ready: false,
      };
    });
    (mappedResponse.user = tempResponse.user),
      (mappedResponse.provider = tempResponse.provider);
    mappedResponse.pass = tempResponse.pass;
    setServers(mappedResponse);
  }, [response]);

  useEffect(() => {
    if (servers) {
      poll();
    }
  }, [servers]);

  return (
    <TidalDashboardServerCreatedModalWrapper>
      {serverReady ? (
        <TidalDashboardServerCreatedModalInfoRow>
          {!!readyServers?.length ? (
            <>
              <div className="icon-wrapper">
                <CopyTick color="#23E588" />
              </div>
              <p className="text">Success</p>
            </>
          ) : (
            <>
              <div className="icon-wrapper-error">
                <CopyTick color="#F35C8A" />
              </div>
              <p className="text-xl font-bold text-red-500">Failed</p>
            </>
          )}
        </TidalDashboardServerCreatedModalInfoRow>
      ) : (
        <TidalDashboardServerCreatedModalInfoRow>
          <div>
            <TailSpin color="#23E588" height={30} width={30} />
            <div style={{ height: 32 }} />
            <p className="text">Creating...</p>
          </div>
        </TidalDashboardServerCreatedModalInfoRow>
      )}
      <TidalDashboardServerCreatedModalTitle>
        {!!readyServers?.length && (
          <p className="title">
            Your instance(s) have started booting up, we will not store your
            login so make sure to <span>save your password.</span>
          </p>
        )}
        {serverReady && servers?.length !== readyServers?.length ? (
          <p className="text-red-500 text-center my-4">
            Unable to boot all servers due to Static IP quota.
          </p>
        ) : (
          <></>
        )}
      </TidalDashboardServerCreatedModalTitle>
      {serverReady && (
        <TidalDashboardServerCreatedModalBody>
          {readyServers?.map((server) => (
            <>
              <TidalDashboardServerCreatedModalBodyRow
                onClick={() => {
                  navigator.clipboard.writeText(server?.ip);
                  toast.info('Copied to clipboard');
                }}
              >
                <p className="left">Host</p>
                <p className="right">{server.ip}</p>
              </TidalDashboardServerCreatedModalBodyRow>
              <TidalDashboardServerCreatedModalBodyRow
                onClick={() => {
                  navigator.clipboard.writeText(server?.auth?.user);
                  toast.info('Copied to clipboard');
                }}
              >
                <p className="left">Username</p>
                <p className="right">{server?.auth?.user}</p>
              </TidalDashboardServerCreatedModalBodyRow>
              <TidalDashboardServerCreatedModalBodyRow
                onClick={() => {
                  navigator.clipboard.writeText(server?.auth?.pass);
                  toast.info('Copied to clipboard');
                }}
              >
                <p className="left">Password</p>
                <div className="right">
                  <div className="key-wrapper">
                    {isObfuscated && (
                      <div className="key-row">
                        <div className="obstructed-pattern">
                          {Array(Math.ceil(server?.auth?.pass?.length))
                            .fill(0)
                            .map((a) => (
                              <div className="cirle"></div>
                            ))}
                        </div>
                      </div>
                    )}
                    {!isObfuscated && (
                      <p className="license-key-text">{server?.auth?.pass}</p>
                    )}
                  </div>
                  <TidalDashboardBotVaultCardObfuscateButton
                    onClick={() => setIsObfuscated(!isObfuscated)}
                  >
                    <Eye />
                  </TidalDashboardBotVaultCardObfuscateButton>
                </div>
              </TidalDashboardServerCreatedModalBodyRow>
              <TidalDashboardServerCreatedModalFooter>
                <div />
                <a
                  href={rdpFile(server.auth.user, server.auth.pass, server.ip)}
                  download={`${server.ip}.rdp`}
                >
                  <TidalButton text="Download RDP" type="info" tint={false} />
                </a>
              </TidalDashboardServerCreatedModalFooter>
            </>
          ))}
          <TidalDashboardServerCreatedModalFooter>
            <div />
            <TidalButton
              text="Close"
              type="default"
              tint={true}
              onClick={onClose}
            />
          </TidalDashboardServerCreatedModalFooter>
        </TidalDashboardServerCreatedModalBody>
      )}
      {/* {serverReady &&
        readyServers?.map((server) => (
          <TidalDashboardServerCreatedModalFooter>
            <TidalButton
              text="Cancel"
              type="default"
              tint={true}
              onClick={onClose}
            />
            <a
              href={rdpFile(server.auth.user, server.auth.pass, server.ip)}
              download={`${rdpFile(
                server.auth.user,
                server.auth.pass,
                server.ip,
              ).replace(/\./g, '_')}.rdp`}
            >
              <TidalButton text="Download RDP" type="info" tint={false} />
            </a>
          </TidalDashboardServerCreatedModalFooter>
        ))} */}
    </TidalDashboardServerCreatedModalWrapper>
  );
};

export default TidalDashboardServerCreatedModal;
